/**
 * Tutorial Accessibility Utilities
 * 
 * Provides accessibility features for the tutorial system including
 * ARIA labels, keyboard navigation, focus management, and screen reader support.
 * 
 * Requirements: 6.5
 */

import type { TutorialStep } from '../types/tutorial';

/**
 * ARIA role definitions for tutorial elements
 */
export const TUTORIAL_ARIA_ROLES = {
  TUTORIAL_CONTAINER: 'dialog',
  TUTORIAL_CONTENT: 'document',
  TUTORIAL_TITLE: 'heading',
  TUTORIAL_DESCRIPTION: 'region',
  TUTORIAL_NAVIGATION: 'navigation',
  TUTORIAL_BUTTON: 'button',
  TUTORIAL_PROGRESS: 'progressbar',
  TUTORIAL_STEP_INDICATOR: 'status',
} as const;

/**
 * ARIA label templates for tutorial elements
 */
export const TUTORIAL_ARIA_LABELS = {
  TUTORIAL_DIALOG: 'Tutorial walkthrough',
  TUTORIAL_CLOSE: 'Close tutorial',
  TUTORIAL_SKIP: 'Skip tutorial',
  TUTORIAL_NEXT: 'Next step',
  TUTORIAL_PREVIOUS: 'Previous step',
  TUTORIAL_FINISH: 'Finish tutorial',
  TUTORIAL_PROGRESS: (current: number, total: number) => 
    `Tutorial progress: step ${current} of ${total}`,
  TUTORIAL_STEP_TITLE: (stepNumber: number, title: string) => 
    `Step ${stepNumber}: ${title}`,
  TUTORIAL_STEP_CONTENT: (stepNumber: number) => 
    `Tutorial step ${stepNumber} content`,
  TUTORIAL_TARGET_HIGHLIGHT: (target: string) => 
    `Highlighted element: ${target}`,
} as const;

/**
 * Keyboard navigation key codes
 */
export const TUTORIAL_KEYBOARD_KEYS = {
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  SPACE: ' ',
  TAB: 'Tab',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  HOME: 'Home',
  END: 'End',
} as const;

/**
 * Focus management utilities
 */
export class TutorialFocusManager {
  private previousActiveElement: Element | null = null;
  private tutorialContainer: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];

  /**
   * Initialize focus management for tutorial
   */
  initialize(tutorialContainer: HTMLElement): void {
    this.tutorialContainer = tutorialContainer;
    this.previousActiveElement = document.activeElement;
    this.updateFocusableElements();
    this.trapFocus();
  }

  /**
   * Clean up focus management
   */
  cleanup(): void {
    this.restorePreviousFocus();
    this.tutorialContainer = null;
    this.focusableElements = [];
  }

  /**
   * Update list of focusable elements within tutorial
   */
  private updateFocusableElements(): void {
    if (!this.tutorialContainer) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
    ].join(', ');

    this.focusableElements = Array.from(
      this.tutorialContainer.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];
  }

  /**
   * Trap focus within tutorial container
   */
  private trapFocus(): void {
    if (!this.tutorialContainer) return;

    this.tutorialContainer.addEventListener('keydown', this.handleKeyDown);
    
    // Focus first focusable element
    if (this.focusableElements.length > 0) {
      this.focusableElements[0].focus();
    }
  }

  /**
   * Handle keyboard navigation within tutorial
   */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === TUTORIAL_KEYBOARD_KEYS.TAB) {
      this.handleTabNavigation(event);
    }
  };

  /**
   * Handle tab navigation to trap focus
   */
  private handleTabNavigation(event: KeyboardEvent): void {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];
    const currentElement = document.activeElement as HTMLElement;

    if (event.shiftKey) {
      // Shift + Tab (backward)
      if (currentElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab (forward)
      if (currentElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * Restore focus to previous element
   */
  private restorePreviousFocus(): void {
    if (this.previousActiveElement && 'focus' in this.previousActiveElement) {
      (this.previousActiveElement as HTMLElement).focus();
    }
  }

  /**
   * Focus specific element within tutorial
   */
  focusElement(selector: string): void {
    if (!this.tutorialContainer) return;

    const element = this.tutorialContainer.querySelector(selector) as HTMLElement;
    if (element) {
      element.focus();
    }
  }

  /**
   * Get current focused element index
   */
  getCurrentFocusIndex(): number {
    const currentElement = document.activeElement as HTMLElement;
    return this.focusableElements.indexOf(currentElement);
  }

  /**
   * Focus next focusable element
   */
  focusNext(): void {
    const currentIndex = this.getCurrentFocusIndex();
    const nextIndex = (currentIndex + 1) % this.focusableElements.length;
    this.focusableElements[nextIndex]?.focus();
  }

  /**
   * Focus previous focusable element
   */
  focusPrevious(): void {
    const currentIndex = this.getCurrentFocusIndex();
    const previousIndex = currentIndex <= 0 
      ? this.focusableElements.length - 1 
      : currentIndex - 1;
    this.focusableElements[previousIndex]?.focus();
  }
}

/**
 * Screen reader announcement utilities
 */
export class TutorialScreenReader {
  private announceElement: HTMLElement | null = null;

  /**
   * Initialize screen reader support
   */
  initialize(): void {
    this.createAnnounceElement();
  }

  /**
   * Clean up screen reader support
   */
  cleanup(): void {
    if (this.announceElement && document.body) {
      document.body.removeChild(this.announceElement);
      this.announceElement = null;
    }
  }

  /**
   * Create hidden element for screen reader announcements
   */
  private createAnnounceElement(): void {
    if (!document.body) {
      return;
    }

    this.announceElement = document.createElement('div');
    this.announceElement.setAttribute('aria-live', 'polite');
    this.announceElement.setAttribute('aria-atomic', 'true');
    this.announceElement.setAttribute('aria-relevant', 'text');
    this.announceElement.style.position = 'absolute';
    this.announceElement.style.left = '-10000px';
    this.announceElement.style.width = '1px';
    this.announceElement.style.height = '1px';
    this.announceElement.style.overflow = 'hidden';
    document.body.appendChild(this.announceElement);
  }

  /**
   * Announce message to screen readers
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.announceElement) return;

    this.announceElement.setAttribute('aria-live', priority);
    this.announceElement.textContent = message;

    // Clear after announcement to allow repeated announcements
    setTimeout(() => {
      if (this.announceElement) {
        this.announceElement.textContent = '';
      }
    }, 1000);
  }

  /**
   * Announce tutorial step change
   */
  announceStepChange(stepNumber: number, totalSteps: number, title: string): void {
    const message = `Tutorial step ${stepNumber} of ${totalSteps}: ${title}`;
    this.announce(message, 'polite');
  }

  /**
   * Announce tutorial completion
   */
  announceTutorialComplete(): void {
    this.announce('Tutorial completed successfully', 'polite');
  }

  /**
   * Announce tutorial skip
   */
  announceTutorialSkipped(): void {
    this.announce('Tutorial skipped', 'polite');
  }

  /**
   * Announce error
   */
  announceError(error: string): void {
    this.announce(`Tutorial error: ${error}`, 'assertive');
  }
}

/**
 * Keyboard navigation handler for tutorial
 */
export class TutorialKeyboardHandler {
  private onNext?: () => void;
  private onPrevious?: () => void;
  private onSkip?: () => void;
  private onClose?: () => void;

  /**
   * Initialize keyboard navigation
   */
  initialize(handlers: {
    onNext?: () => void;
    onPrevious?: () => void;
    onSkip?: () => void;
    onClose?: () => void;
  }): void {
    this.onNext = handlers.onNext;
    this.onPrevious = handlers.onPrevious;
    this.onSkip = handlers.onSkip;
    this.onClose = handlers.onClose;

    document.addEventListener('keydown', this.handleGlobalKeyDown);
  }

  /**
   * Clean up keyboard navigation
   */
  cleanup(): void {
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
  }

  /**
   * Handle global keyboard events for tutorial
   */
  private handleGlobalKeyDown = (event: KeyboardEvent): void => {
    // Only handle if tutorial is active and not in an input field
    const activeElement = document.activeElement;
    const isInputField = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.getAttribute('contenteditable') === 'true'
    );

    if (isInputField) return;

    switch (event.key) {
      case TUTORIAL_KEYBOARD_KEYS.ESCAPE:
        event.preventDefault();
        this.onClose?.();
        break;

      case TUTORIAL_KEYBOARD_KEYS.ARROW_RIGHT:
      case TUTORIAL_KEYBOARD_KEYS.SPACE:
        if (!event.shiftKey) {
          event.preventDefault();
          this.onNext?.();
        }
        break;

      case TUTORIAL_KEYBOARD_KEYS.ARROW_LEFT:
        event.preventDefault();
        this.onPrevious?.();
        break;

      case TUTORIAL_KEYBOARD_KEYS.ENTER:
        // Let default behavior handle button clicks
        break;

      case 's':
      case 'S':
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          this.onSkip?.();
        }
        break;
    }
  };
}

/**
 * Generate accessibility attributes for tutorial elements
 */
export function generateTutorialAccessibilityAttributes(
  step: TutorialStep,
  stepNumber: number,
  totalSteps: number
): Record<string, string> {
  return {
    'role': TUTORIAL_ARIA_ROLES.TUTORIAL_CONTAINER,
    'aria-modal': 'true',
    'aria-labelledby': `tutorial-title-${step.id}`,
    'aria-describedby': `tutorial-content-${step.id}`,
    'aria-live': 'polite',
    'aria-atomic': 'true',
    'data-step-number': stepNumber.toString(),
    'data-total-steps': totalSteps.toString(),
  };
}

/**
 * Generate accessibility attributes for tutorial title
 */
export function generateTutorialTitleAttributes(
  step: TutorialStep,
  stepNumber: number
): Record<string, string> {
  return {
    'id': `tutorial-title-${step.id}`,
    'role': TUTORIAL_ARIA_ROLES.TUTORIAL_TITLE,
    'aria-level': '2',
    'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_STEP_TITLE(stepNumber, String(step.title || '')),
  };
}

/**
 * Generate accessibility attributes for tutorial content
 */
export function generateTutorialContentAttributes(
  step: TutorialStep,
  stepNumber: number
): Record<string, string> {
  return {
    'id': `tutorial-content-${step.id}`,
    'role': TUTORIAL_ARIA_ROLES.TUTORIAL_DESCRIPTION,
    'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_STEP_CONTENT(stepNumber),
  };
}

/**
 * Generate accessibility attributes for tutorial buttons
 */
export function generateTutorialButtonAttributes(
  buttonType: 'next' | 'previous' | 'skip' | 'close' | 'finish',
  isDisabled: boolean = false
): Record<string, string> {
  const baseAttributes = {
    'role': TUTORIAL_ARIA_ROLES.TUTORIAL_BUTTON,
    'type': 'button',
    'aria-disabled': isDisabled.toString(),
  };

  switch (buttonType) {
    case 'next':
      return {
        ...baseAttributes,
        'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_NEXT,
        'aria-keyshortcuts': 'ArrowRight Space',
      };
    case 'previous':
      return {
        ...baseAttributes,
        'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_PREVIOUS,
        'aria-keyshortcuts': 'ArrowLeft',
      };
    case 'skip':
      return {
        ...baseAttributes,
        'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_SKIP,
        'aria-keyshortcuts': 'Ctrl+S',
      };
    case 'close':
      return {
        ...baseAttributes,
        'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_CLOSE,
        'aria-keyshortcuts': 'Escape',
      };
    case 'finish':
      return {
        ...baseAttributes,
        'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_FINISH,
        'aria-keyshortcuts': 'Enter',
      };
    default:
      return baseAttributes;
  }
}

/**
 * Generate accessibility attributes for tutorial progress indicator
 */
export function generateTutorialProgressAttributes(
  currentStep: number,
  totalSteps: number
): Record<string, string> {
  return {
    'role': TUTORIAL_ARIA_ROLES.TUTORIAL_PROGRESS,
    'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_PROGRESS(currentStep, totalSteps),
    'aria-valuenow': currentStep.toString(),
    'aria-valuemin': '1',
    'aria-valuemax': totalSteps.toString(),
    'aria-valuetext': `Step ${currentStep} of ${totalSteps}`,
  };
}

/**
 * Generate accessibility attributes for highlighted target element
 */
export function generateTargetHighlightAttributes(
  target: string
): Record<string, string> {
  return {
    'aria-describedby': 'tutorial-highlight-description',
    'aria-label': TUTORIAL_ARIA_LABELS.TUTORIAL_TARGET_HIGHLIGHT(target),
    'role': 'region',
  };
}

/**
 * Check if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if user prefers high contrast
 */
export function prefersHighContrast(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia('(prefers-contrast: high)').matches;
}

/**
 * Get appropriate animation duration based on user preferences
 */
export function getAccessibleAnimationDuration(defaultDuration: number): number {
  return prefersReducedMotion() ? 0 : defaultDuration;
}

/**
 * Comprehensive tutorial accessibility manager
 */
export class TutorialAccessibilityManager {
  private focusManager: TutorialFocusManager;
  private screenReader: TutorialScreenReader;
  private keyboardHandler: TutorialKeyboardHandler;

  constructor() {
    this.focusManager = new TutorialFocusManager();
    this.screenReader = new TutorialScreenReader();
    this.keyboardHandler = new TutorialKeyboardHandler();
  }

  /**
   * Initialize all accessibility features
   */
  initialize(
    tutorialContainer: HTMLElement,
    handlers: {
      onNext?: () => void;
      onPrevious?: () => void;
      onSkip?: () => void;
      onClose?: () => void;
    }
  ): void {
    this.focusManager.initialize(tutorialContainer);
    this.screenReader.initialize();
    this.keyboardHandler.initialize(handlers);
  }

  /**
   * Clean up all accessibility features
   */
  cleanup(): void {
    this.focusManager.cleanup();
    this.screenReader.cleanup();
    this.keyboardHandler.cleanup();
  }

  /**
   * Handle step change with accessibility announcements
   */
  handleStepChange(stepNumber: number, totalSteps: number, title: string): void {
    this.screenReader.announceStepChange(stepNumber, totalSteps, title);
  }

  /**
   * Handle tutorial completion
   */
  handleTutorialComplete(): void {
    this.screenReader.announceTutorialComplete();
  }

  /**
   * Handle tutorial skip
   */
  handleTutorialSkip(): void {
    this.screenReader.announceTutorialSkipped();
  }

  /**
   * Handle tutorial error
   */
  handleTutorialError(error: string): void {
    this.screenReader.announceError(error);
  }

  /**
   * Get focus manager instance
   */
  getFocusManager(): TutorialFocusManager {
    return this.focusManager;
  }

  /**
   * Get screen reader instance
   */
  getScreenReader(): TutorialScreenReader {
    return this.screenReader;
  }

  /**
   * Get keyboard handler instance
   */
  getKeyboardHandler(): TutorialKeyboardHandler {
    return this.keyboardHandler;
  }
}